import { useEffect, useRef } from "react";

const services = [
  {
    number: "01",
    icon: "ri-user-search-line",
    title: "AI Lead Capture & Qualification",
    desc: "Automated intake systems that identify, score, and route high-value prospects before human review — reducing response lag and improving conversion at the top of funnel.",
    metric: "3× faster intake",
  },
  {
    number: "02",
    icon: "ri-git-branch-line",
    title: "CRM & Pipeline Automation",
    desc: "Eliminate manual CRM entry and pipeline stagnation. Automated workflows keep deals moving, trigger follow-ups, and surface at-risk opportunities in real time.",
    metric: "Zero manual entry",
  },
  {
    number: "03",
    icon: "ri-file-list-3-line",
    title: "Underwriting & Workflow Systems",
    desc: "Streamlined data collection, document processing, and conditional logic that accelerates qualification and reduces underwriting turnaround from days to hours.",
    metric: "Days → Hours",
  },
  {
    number: "04",
    icon: "ri-shield-check-line",
    title: "Compliance & Reporting Automation",
    desc: "Structured audit trails, automated reporting, and compliance checkpoints built directly into operational workflows — keeping your team accountable without added overhead.",
    metric: "Full audit trail",
  },
];

export default function ServicesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll<HTMLElement>("[data-reveal]");
            els.forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = "1";
                el.style.transform = "translateY(0)";
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="bg-bfx-light" ref={sectionRef}>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-32">
        {/* Header */}
        <div className="text-center mb-20">
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bfx-accent mb-4"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Services
          </p>
          <h2
            data-reveal
            className="text-4xl md:text-5xl lg:text-[58px] font-semibold text-bfx-primary leading-[1.05] tracking-tight mb-5"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Intelligence Infrastructure.
          </h2>
          <p
            data-reveal
            className="text-base text-bfx-muted max-w-md mx-auto leading-relaxed"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            End-to-end systems built for financial operators who need results — not experiments.
          </p>
        </div>

        {/* Service rows — full-width divider style */}
        <div className="flex flex-col">
          {services.map((s, i) => (
            <div
              key={s.number}
              data-reveal
              className="group grid grid-cols-1 md:grid-cols-[64px_1fr_auto] gap-4 md:gap-10 items-start py-10 border-t border-bfx-border last:border-b hover:bg-white/60 transition-all duration-300 rounded-xl px-6 cursor-default"
              style={{ opacity: 0, transform: "translateY(16px)" }}
            >
              {/* Number */}
              <span className="text-[13px] font-semibold text-bfx-accent tracking-widest pt-1">
                {s.number}
              </span>

              {/* Main content */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center text-bfx-secondary shrink-0">
                    <i className={`${s.icon} text-base`} />
                  </div>
                  <h3 className="text-lg font-semibold text-bfx-primary leading-snug">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm text-bfx-muted leading-relaxed max-w-xl">{s.desc}</p>
              </div>

              {/* Metric pill */}
              <div className="shrink-0 self-center mt-2 md:mt-0">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bfx-accent/10 border border-bfx-accent/20">
                  <div className="w-1.5 h-1.5 rounded-full bg-bfx-accent shrink-0" />
                  <span className="text-xs font-semibold text-bfx-accent whitespace-nowrap">{s.metric}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}